import { FileText, Upload, X } from 'lucide-react';
import * as React from 'react';
import { date } from '../lib/format';
import { Button } from './button';

// Files people upload (onboarding documents, supporting documents on a payment): PDF, JPG or PNG up to 10 MB. The
// browser checks type and size first so the person hears at once; the server checks the real type again and scans.

export const ACCEPTED_FILES = 'application/pdf,image/jpeg,image/png';
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const FILE_RULES = 'PDF, JPG or PNG, up to 10 MB.';

/** Why a file cannot be uploaded, or null. */
export function fileProblem(file: File): string | null {
  if (!ACCEPTED_FILES.split(',').includes(file.type)) return `${file.name}: only PDF, JPG and PNG files are accepted.`;
  if (file.size > MAX_FILE_BYTES) return `${file.name}: the file is larger than 10 MB.`;
  return null;
}

/** A button that opens the file picker; `multiple` lets several files be chosen at once. */
export function FileButton({ id, label, busy, multiple, disabled, onFiles }: {
  id: string; label: string; busy: boolean; multiple?: boolean; disabled?: boolean; onFiles: (files: File[]) => void;
}) {
  const input = React.useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={input} type="file" accept={ACCEPTED_FILES} multiple={multiple} className="sr-only" id={id} aria-label={label}
        onChange={e => { onFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
      <Button type="button" size="sm" variant="secondary" disabled={busy || disabled} onClick={() => input.current?.click()}>
        <Upload className="size-4" aria-hidden />{busy ? 'Uploading…' : label}
      </Button>
    </>
  );
}

const size = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Files attached to something: name (opens it when `onOpen` is given), an optional tag (e.g. where it came from), size
 *  and date, and Remove when `onRemove` is given. */
export function AttachmentList({ files, onOpen, onRemove, label = 'Attached documents' }: {
  files: Array<{ id: string; fileName: string; sizeBytes: number; uploadedAt: string; tag?: string }>;
  onOpen?: (id: string) => void; onRemove?: (id: string) => void; label?: string;
}) {
  if (!files.length) return null;
  return (
    <ul aria-label={label} className="divide-y divide-line rounded-lg border border-line">
      {files.map(f => (
        <li key={f.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <FileText className="size-4 shrink-0 text-ink-3" aria-hidden />
          {onOpen
            ? <button type="button" className="min-w-0 truncate text-left font-semibold text-tick-ink underline" onClick={() => onOpen(f.id)}>{f.fileName}</button>
            : <span className="min-w-0 truncate font-semibold text-ink">{f.fileName}</span>}
          {f.tag && <span className="shrink-0 rounded-full bg-mint-2 px-2 py-0.5 text-xs text-ink-2">{f.tag}</span>}
          <span className="ml-auto shrink-0 text-xs text-ink-3">{size(f.sizeBytes)} · {date(f.uploadedAt)}</span>
          {onRemove && (
            <button type="button" className="shrink-0 rounded p-1 text-ink-3 hover:text-ink" aria-label={`Remove ${f.fileName}`} onClick={() => onRemove(f.id)}>
              <X className="size-4" aria-hidden />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
